/**
 * CR-7781: the one place a call-off status is named and coloured.
 *
 * "Confirmed" alone was read as the haulier having confirmed, when it is the
 * supplier. So the stored value `confirmed` reads Supplier Confirmed, and the
 * haulier's own Confirm Load is a second indicator, Logistics Confirmed, shown
 * beside the status rather than replacing it. Every screen — the customer's
 * grid, tiles and detail, the supplier's, the haulier's list and the export —
 * takes its wording from here, so the three sides cannot drift apart again.
 */
import type { LucideIcon } from 'lucide-react';
import { CheckCircle, ClipboardCheck, Package, Send, Truck, XCircle } from 'lucide-react';

export interface CallOffStatusMeta {
  label: string;
  /** Badge colours, light and dark. */
  className: string;
  icon: LucideIcon;
}

export const CALL_OFF_STATUS_META: Record<string, CallOffStatusMeta> = {
  draft: { label: 'Draft', className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200', icon: Package },
  pending: { label: 'Pending', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200', icon: Send },
  confirmed: { label: 'Supplier Confirmed', className: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200', icon: CheckCircle },
  dispatched: { label: 'Dispatched', className: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-200', icon: Truck },
  delivered: { label: 'Delivered', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200', icon: Package },
  cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200', icon: XCircle },
};

/** The haulier's Confirm Load, shown beside the call-off's own status. */
export const LOGISTICS_CONFIRMED_META: CallOffStatusMeta = {
  label: 'Logistics Confirmed',
  className: 'bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-200',
  icon: ClipboardCheck,
};

/**
 * The display label for a call-off status.
 *
 * @param status - a `detail_status` value
 * @returns its label, or the raw value capitalised for one this map lacks
 */
export function callOffStatusLabel(status: string): string {
  const meta = CALL_OFF_STATUS_META[status];
  if (meta) return meta.label;
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : '';
}

/**
 * The status as one line of text — the status, plus Logistics Confirmed once
 * the haulier has confirmed. For the export, where two badges become one cell.
 *
 * @param status - a `detail_status` value
 * @param logisticsConfirmedAt - the call-off's `logistics_confirmed_at`
 * @returns e.g. "Supplier Confirmed · Logistics Confirmed"
 */
export function callOffStatusText(status: string, logisticsConfirmedAt?: string | null): string {
  const label = callOffStatusLabel(status);
  return logisticsConfirmedAt ? `${label} · ${LOGISTICS_CONFIRMED_META.label}` : label;
}
